import React, { useRef, useState } from "react";
import { Table, Input, Button, Space } from "antd";
import type { InputRef, TableColumnType, TableColumnsType } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { FilterConfirmProps } from 'antd/es/table/interface';
import "./data_work_order_allocation.css";
import useSWR from "swr";
import axios from "axios";

interface ModalProps {
     workOrderData?: any[];
     isLoading?: boolean;
     onRowClick?: (record: any) => void;
}

type DataIndex = keyof any;

const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const ModalDataWorkOrderAllocation: React.FC<ModalProps> = ({ workOrderData, isLoading, onRowClick }) => {
     const [searchText, setSearchText] = useState('');
     const [searchedColumn, setSearchedColumn] = useState('');
     const searchInput = useRef<InputRef>(null);
     const { data: swrWorkOrderData, isLoading: isSwrLoading } = useSWR(
          `${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/work-order-list`,
          fetcher
     );

     const handleSearch = (
          selectedKeys: string[],
          confirm: (param?: FilterConfirmProps) => void,
          dataIndex: DataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex as string);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText('');
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<any> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${dataIndex.toString()}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}
                         >
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}
                         >
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex as string);
                              }}
                         >
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}
                         >
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => (
               <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
          ),
          onFilter: (value, record) =>
               record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
          onFilterDropdownOpenChange: (visible) => {
               if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
               }
          },
     });

     const columns: TableColumnsType<any> = [
          {
               title: "Work Order No",
               dataIndex: "work_order_no",
               key: "work_order_no",
               sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no),
               ...getColumnSearchProps('work_order_no'),
          },
          {
               title: "Work Order Date",
               dataIndex: "work_order_date",
               key: "work_order_date",
               render: (text: string) => <span>{text || "-"}</span>,
               sorter: (a, b) => (a.work_order_date || '').localeCompare(b.work_order_date || ''),
               ...getColumnSearchProps('work_order_date'),
          },
          {
               title: "Model",
               dataIndex: "model_description",
               key: "model",
               sorter: (a, b) => (a.model_description || '').localeCompare(b.model_description || ''),
               ...getColumnSearchProps('model_description'),
               render: (text: string, record: any) => (
                    <span title={record.model}>{text || record.model || "-"}</span>
               ),
          },
          {
               title: "Chassis No",
               dataIndex: "chassis_no",
               key: "chassis_no",
               sorter: (a, b) => a.chassis_no.localeCompare(b.chassis_no),
               ...getColumnSearchProps('chassis_no'),
          },
          {
               title: "No Polisi",
               dataIndex: "no_polisi",
               key: "no_polisi",
               sorter: (a, b) => a.no_polisi.localeCompare(b.no_polisi),
               ...getColumnSearchProps('no_polisi'),
          },
          {
               title: "Service Advisor",
               dataIndex: "service_advisor",
               key: "service_advisor",
               sorter: (a, b) => a.service_advisor.localeCompare(b.service_advisor),
               ...getColumnSearchProps('service_advisor'),
          },
          {
               title: "Warehouse",
               dataIndex: "warehouse_name",
               key: "warehouse",
               sorter: (a, b) => (a.warehouse_name || '').localeCompare(b.warehouse_name || ''),
               ...getColumnSearchProps('warehouse_name'),
          },
     ];

     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

     return (
          <div id="i6ho" style={transformStyle}>
               <div className="gjs-grid-row" id="i5gm">
                    <div className="gjs-grid-column" id="i2yh">
                         <Table
                              columns={columns}
                              dataSource={(workOrderData || swrWorkOrderData)?.filter((item: any) => item.status === '2') || []}
                              loading={isLoading !== undefined ? isLoading : isSwrLoading}
                              pagination={{ pageSize: 5 }}
                              size="small"
                              bordered
                              rowKey="work_order_no"
                              onRow={(record) => ({
                                   onDoubleClick: () => onRowClick?.(record),
                                   style: { cursor: 'pointer' }
                              })}
                         />
                    </div>
               </div>
          </div>
     );
};

export default ModalDataWorkOrderAllocation;